import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Button,
  Stack,
} from "@mui/material";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  createProductOrderRequest,
  resetProductOrderState,
} from "../../../redux/ProductOrder/productOrderSlice";
import GatewaySelectionDialog from "../../Common/PaymentGatewaySelectionDialog";
import {
  getPaymentPlatform,
  normalizeGateway,
  savePaymentMeta,
  startPaymentFromOrderResponse,
} from "../../Common/PaymentGatewayMethods";
import { calculateProductPriceFromProduct } from "../../../Utils/productPriceCalculator";
import { getBullionRatesRequest } from "../../../redux/Dashboard/dashboardSlice";
import { useAppTheme } from "../../../theme/useAppTheme";
import ProductMediaThumb from "../ProductMediaPreview";
import useCurrency from "../../../hooks/useCurrency";

const PriceRow = ({ label, value, percent, textColor }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    gap={1}
    py={0.75}
  >
    <Typography sx={{ fontWeight: 500, color: textColor, flex: 1 }}>
      {label}
    </Typography>
    {percent != null && (
      <Typography
        sx={{
          color: "#3DDC84",
          fontWeight: 600,
          fontSize: 13,
          whiteSpace: "nowrap",
        }}
      >
        ({Number(percent).toFixed(1)}%)
      </Typography>
    )}
    <Typography
      sx={{
        fontWeight: 700,
        color: textColor,
        whiteSpace: "nowrap",
        minWidth: 88,
        textAlign: "right",
      }}
    >
      {value}
    </Typography>
  </Stack>
);

export default function ProductDetailsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { appConfig, bullionRates } = useSelector((s) => s.dashboard || {});
  const productOrder = useSelector((state) => state.productOrder);
  const { customer: fetchedCustomer } = useSelector(
    (state) => state.profile || {},
  );
  const customer = fetchedCustomer || {};

  const product = state?.product;
  const paymentGateways = appConfig?.paymentGateways || [];
  const [openGatewayDialog, setOpenGatewayDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [selectedProductForPay, setSelectedProductForPay] = useState(null);

  const taxtype = appConfig?.taxtype ?? "GST";

  const themeColors = useAppTheme();

  const { symbol } = useCurrency();

  useEffect(() => {
    dispatch(resetProductOrderState());
  }, [dispatch]);

  useEffect(() => {
    if (!bullionRates?.length) {
      dispatch(
        getBullionRatesRequest({ id: `${process.env.REACT_APP_JEWELLER_ID}` }),
      );
    }
  }, [dispatch, bullionRates?.length]);

  const getMetalRate = () => {
    if (!Array.isArray(bullionRates) || !product) return 0;

    const bullionId = product.bullion?._id || product.bullion?.id;
    const byId = bullionRates.find(
      (item) =>
        item.rateType === "Buy" &&
        item.bullion &&
        (item.bullion._id === bullionId || item.bullion.id === bullionId),
    );
    if (byId?.ratePerGram) return Number(byId.ratePerGram);

    const rateObj = bullionRates.find((item) => {
      return (
        item.rateType === "Buy" &&
        item.bullion?.metalName?.toLowerCase() ===
          (product.metalname || product.bullion?.metalName)?.toLowerCase() &&
        Number(item.bullion?.purity) ===
          Number(product.bullion?.purity ?? product.purity)
      );
    });

    return Number(rateObj?.ratePerGram || 0);
  };

  const price = calculateProductPriceFromProduct(product, getMetalRate());

  const money = (value) =>
    `${symbol}${Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  useEffect(() => {
    if (productOrder.error) {
      toast.error(productOrder.error?.message || productOrder.error || "Order creation failed");
      setLoading(false);
    }
  }, [productOrder.error]);

  useEffect(() => {
    if (!productOrder.productResponse || !selectedGateway) return;

    const pay = async () => {
      setLoading(true);
      await startPaymentFromOrderResponse(
        productOrder.productResponse,
        selectedGateway,
      );
      setLoading(false);
    };

    pay();
  }, [productOrder.productResponse, selectedGateway]);

  const handlePayOnline = (product = null) => {
    if (!paymentGateways.length) {
      toast.warn("No payment gateways configured!");
      return;
    }
    setSelectedProductForPay(product);
    if (paymentGateways.length === 1) {
      handleGatewaySelect(paymentGateways[0]);
      return;
    }
    setOpenGatewayDialog(true);
  };

  const handleGatewaySelect = async (gateway) => {
    setOpenGatewayDialog(false);
    if (!gateway) return;

    const normalized = normalizeGateway(gateway);
    setSelectedGateway(normalized);
    setLoading(true);

    try {
      const payload = {
        product: product._id,
        amount: price.finalPrice,
        metalName: "Product",
        orderType: "Purchase",
        weight: product.metalWeight ?? product.metal_weight,
        buyType: "Online",
        transactionMode: normalized,
        x_api_version: "2022-09-01",
        platform: getPaymentPlatform(),
        customernote: "",
        customer: customer,
      };
      savePaymentMeta(
        {
          grams: payload.weight,
          metal: payload.metalName,
          metalName: payload.metalName,
          amount: payload.amount,
          totalAmount: payload.amount,
          tax: Number(price.taxAmount || 0),
          selectedGateway: normalized,
          orderType: payload.orderType,
          buyType: payload.buyType,
          planid: "",
          moneysip: false,
        },
        { replace: true },
      );
      dispatch(resetProductOrderState());
      dispatch(createProductOrderRequest(payload));
    } catch (err) {
      console.error("order create error", err);
      toast.error("Payment initialization failed.");
      setLoading(false);
      setSelectedProductForPay(null);
    }
  };

  if (!product) {
    return (
      <Typography sx={{ p: 4, textAlign: "center" }}>
        No product data found!
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: themeColors.scaffoldGradientColors,
        border: `1px solid ${themeColors.scaffold}`,
        p: { xs: 2, md: 4 },
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 900,
          borderRadius: 3,
          boxShadow: 5,
          p: 3,
          background: themeColors.bigContainer,
          border: `1px solid ${themeColors.scaffold}`,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            textAlign: "center",
            color: themeColors.primary,
            mb: 2,
          }}
        >
          Product Details
        </Typography>

        <Card
          sx={{
            borderRadius: 3,
            boxShadow: 3,
            background: themeColors.mediumContainer,
            border: `1px solid ${themeColors.scaffold}`,
            mb: 3,
          }}
        >
          <CardContent>
            {/* PRODUCT NAME + IMAGE */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, mb: 1, color: themeColors.text }}
                >
                  {product.name}
                </Typography>

                <Typography
                  sx={{ fontSize: 15, mb: 1, color: themeColors.text }}
                >
                  {product.metalname || product.bullion?.metalName || "Gold"} •{" "}
                  {product.purity || product.bullion?.purity} •{" "}
                  {product.bullion?.karat || product.carat}K |{" "}
                  {product.metalWeight ?? product.metal_weight} gms
                </Typography>

                {product?.desc && (
                  <Typography
                    sx={{
                      fontSize: 14,
                      mt: 1,
                      color: themeColors.text,
                    }}
                  >
                    {product.desc}
                  </Typography>
                )}
              </Box>

              <Box sx={{ textAlign: "center" }}>
                <ProductMediaThumb
                  urls={product.url || product.images}
                  videos={product.videos}
                  name={product.name}
                  width={130}
                  height={130}
                  borderRadius="20px"
                />
              </Box>
            </Box>

            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate("/productPriceCalculator")}
              sx={{
                background: themeColors.primary,
                border: `1px solid ${themeColors.scaffold}`,
                mt: 3,
                py: 1.3,
                borderRadius: 2,
                fontWeight: 600,
                color: themeColors.text,
              }}
            >
              Investment Calculator
            </Button>
          </CardContent>
        </Card>

        {/* PRICE DETAILS */}
        <Card
          sx={{
            borderRadius: 3,
            p: 2,
            boxShadow: 3,
            background: themeColors.mediumContainer,
            border: `1px solid ${themeColors.scaffold}`,
            color: themeColors.text,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Price Details
          </Typography>

          <Divider sx={{ mb: 1.5 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Product Cost
          </Typography>
          <PriceRow
            label="Product Price :"
            value={money(price.productCost)}
            textColor={themeColors.text}
          />
          {price.productDiscountPercentage > 0 && (
            <PriceRow
              label="Discounted Product Price :"
              percent={price.productDiscountPercentage}
              value={money(price.discountedProductCost)}
              textColor={themeColors.text}
            />
          )}
          {price.taxPercentage > 0 && (
            <PriceRow
              label={taxtype === "GST" ? "GST on Product :" : "Tax on Product :"}
              percent={price.taxPercentage}
              value={money(price.taxAmount)}
              textColor={themeColors.text}
            />
          )}

          {price.deliverycharges > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Delivery Charges
              </Typography>
              <PriceRow
                label="Delivery Charges :"
                value={money(price.deliverycharges)}
                textColor={themeColors.text}
              />
              {price.deliveryDiscountPercentage > 0 && (
                <PriceRow
                  label="Discounted Delivery Charges :"
                  percent={price.deliveryDiscountPercentage}
                  value={money(price.discountondelivery)}
                  textColor={themeColors.text}
                />
              )}
              {price.gstDeliveryPercentage > 0 && (
                <PriceRow
                  label={
                    taxtype === "GST" ? "GST on Delivery :" : "Tax on Delivery :"
                  }
                  percent={price.gstDeliveryPercentage}
                  value={money(price.gstondeliverycharges)}
                  textColor={themeColors.text}
                />
              )}
              <PriceRow
                label="Final Delivery Charges :"
                value={money(price.finalDeliveryCharges)}
                textColor={themeColors.text}
              />
            </>
          )}

          {price.makingcharges > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Making Charges
              </Typography>
              <PriceRow
                label="Making Charges :"
                value={money(price.makingcharges)}
                textColor={themeColors.text}
              />
              {price.makingDiscountPercentage > 0 && (
                <PriceRow
                  label="Discounted Making Charges :"
                  percent={price.makingDiscountPercentage}
                  value={money(price.discountonmaking)}
                  textColor={themeColors.text}
                />
              )}
              {price.gstMakingPercentage > 0 && (
                <PriceRow
                  label={
                    taxtype === "GST"
                      ? "GST on Making charges :"
                      : "Tax on Making charges :"
                  }
                  percent={price.gstMakingPercentage}
                  value={money(price.gstonmakingcharges)}
                  textColor={themeColors.text}
                />
              )}
              <PriceRow
                label="Final Making Charges :"
                value={money(price.finalmakingcharges)}
                textColor={themeColors.text}
              />
            </>
          )}

          {price.packingcharges > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Packing Charges
              </Typography>
              <PriceRow
                label="Packing Charges :"
                value={money(price.packingcharges)}
                textColor={themeColors.text}
              />
              {price.packingDiscountPercentage > 0 && (
                <PriceRow
                  label="Discounted Packing Charges :"
                  percent={price.packingDiscountPercentage}
                  value={money(price.discountonpacking)}
                  textColor={themeColors.text}
                />
              )}
              {price.gstPackingPercentage > 0 && (
                <PriceRow
                  label={
                    taxtype === "GST"
                      ? "GST on Packing Charges :"
                      : "Tax on Packing Charges :"
                  }
                  percent={price.gstPackingPercentage}
                  value={money(price.gstonpackingcharges)}
                  textColor={themeColors.text}
                />
              )}
              <PriceRow
                label="Final Packing Charges :"
                value={money(price.finalPackingCharges)}
                textColor={themeColors.text}
              />
            </>
          )}

          {(price.hallmarkcharges > 0 || price.othercharges > 0) && (
            <Divider sx={{ my: 1.5 }} />
          )}
          {price.hallmarkcharges > 0 && (
            <PriceRow
              label="Hallmark Charges :"
              value={money(price.hallmarkcharges)}
              textColor={themeColors.text}
            />
          )}
          {price.othercharges > 0 && (
            <PriceRow
              label="Other Charges :"
              value={money(price.othercharges)}
              textColor={themeColors.text}
            />
          )}

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Final Price
          </Typography>
          <PriceRow
            label="Final Price :"
            value={money(price.finalPrice)}
            textColor={themeColors.text}
          />
        </Card>

        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderRadius: 3,
            boxShadow: 3,
            background: themeColors.svgColor,
            color: themeColors.text,
            border: `1px solid ${themeColors.scaffold}`,
          }}
        >
          <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
            Price: {money(price.finalPrice)}
          </Typography>

          <Button
            variant="contained"
            sx={{
              background: themeColors.primary,
              color: themeColors.text,
              border: `1px solid ${themeColors.scaffold}`,
              px: 4,
              py: 1.2,
              borderRadius: 2,
              fontSize: 16,
              fontWeight: 600,
            }}
            onClick={() => handlePayOnline(price)}
            disabled={loading}
          >
            {loading ? "Processing..." : "Buy Now"}
          </Button>
        </Box>
      </Card>
      <GatewaySelectionDialog
        open={openGatewayDialog}
        onClose={handleGatewaySelect}
        gateways={paymentGateways}
      />
    </Box>
  );
}
